import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { navigationConfig } from '../../views/navigation/NavigationConfig';
import { NavigationSubMenuItem } from 'types/NavigationTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isHomepageAllowed } from 'services/routes/isHomePageAllowed';
import { useGlobalContext } from 'GlobalContext';
import TopBar from './TopBar';
import { Avatar } from './Avatar';
import NotificationBell from './NotificationBell';
import ThemeSwitcher from './ThemeSwitcher';
import TimerButton from './TimerButton';
import { useKnowledgeBaseContactForm } from '../../views/knowledge_base/KnowledgeBaseContactForm';
import { fetchData } from 'services/api/fetchData';
import Dropdown from 'components/forms/basefields/Dropdown';
import logo from '../../assets/crafter-logo-header.png';
import '../../style/scss/navigation.scss';

interface Environment {
    environment_hash: string;
    name: string;
    type: string;
    status: string;
}

const Navbar: React.FC = () => {
    const { t } = useTranslation();
    const { isTopNavbarEnabled } = useGlobalContext();
    const { isLoggedIn, firstName, lastName, packageFeatures, activeFeatures, roleFeatures, allowedFeatures, 
            allowedRights, redirectTo, environmentType, environmentHash, handleSwitchEnvironment, handleLogout } = useAuthContext();
    const { userPreferences } = useSettings();
    const openKnowledgeBaseContactForm = useKnowledgeBaseContactForm();
    const [userName, setUserName] = useState<string | null>(null);
    const [profilePicture, setProfilePicture] = useState<string | null>(null);
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const currentLocation = useLocation();
    const trialWizard = redirectTo?.includes('trial-wizard');

    // Determine to hide the navbar for the following routes
    const hideNavBar = currentLocation.pathname.includes('trial-wizard') || currentLocation.pathname.startsWith('/kennisbank');

    // Determine whether the user is a superuser or staff user which can switch environments
    const canSwitchEnvironment = allowedRights.includes('no_user') || allowedRights.includes('staff_user');

    // Fetch environments to switch
    useEffect(() => {
        const fetchEnvironments = async () => {
            if (canSwitchEnvironment) {
                try {
                    const response = await fetchData({
                        apiUrl: 'get_environment_switch_as_superuser_list',
                        handleLogout
                    });

                    setEnvironments(response.results || []);
                } catch (error) {
                    console.error(error);
                }
            }
        };

        fetchEnvironments();
    }, [environmentType, allowedRights]);

    // Set the profile picture if the user preferences changes
    useEffect(() => {
        if (userPreferences) setProfilePicture(userPreferences.profile_picture);
    }, [userPreferences]);

    // Get the name of the logged in user from the local storage
    useEffect(() => {
        if (firstName && lastName) {
            setUserName(`${firstName} ${lastName}`);
        } else if (firstName) {
            setUserName(`${firstName}`)
        } else {
            setUserName(t('account.my_account_label'))
        }
    }, [isLoggedIn, firstName, lastName]);

    // Listens to click outside to close the dropdown
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (dropdownVisible && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownVisible(false);
            }
        };
        window.addEventListener('click', handleClickOutside);
        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownVisible]);

    // Checks if the current route is a sub menu of a main menu
    const isSubrouteOfMenu = (route: string, menu: string) => {
        if(route === "/" && navigationConfig[menu]?.subMenu?.some(subItem => subItem.route === "/home")) {
            return true;
        } else {
            return navigationConfig[menu]?.subMenu?.some(subItem => 
                route === subItem.route || route.startsWith(subItem.route + '/')
            );
        }
    }

    // Derive the selected menu from the current location in the same render, so the
    // sub navbar appears together with the page and pages measuring the nav height see it
    const selectedMenu = useMemo(() => {
        const currentRoute = currentLocation.pathname;
        return Object.keys(navigationConfig).find(menu => 
            navigationConfig[menu].route === currentRoute || isSubrouteOfMenu(currentRoute, menu)
        ) || null;
    }, [currentLocation.pathname]);

    // Check if the feature is active in the environment and the user is allowed to see it based on its role
    const checkActiveFeature = (featureName: string) => {
        const isActive = activeFeatures.includes(featureName);
        const isRoleSpecific = roleFeatures.includes(featureName);
        const isReleased = packageFeatures.includes(featureName);

        // Handle if it's a role specific feature
        if (isRoleSpecific) {

            // Check if the feature is in the activeFeatures, allowedFeatures and if it's released
            return isActive && allowedFeatures.includes(featureName) && isReleased;
        }

        // For normal features, check if it is in the activeFeatures and if it's released
        return isActive && isReleased;
    };

    // Helper function to determine if the sub item of the main item should be visible
    const isSubItemVisible = (item: NavigationSubMenuItem) => {
        // Hide the homepages based on the environment type and rights
        if (!isHomepageAllowed(item.route, environmentType, allowedRights)) {
            return false;
        }

        // Hide the menu item if the allowed feature is not active in the role
        if (item.allowedFeature && !allowedFeatures.includes(item.allowedFeature)) {
            return false;
        }

        // Hide the menu item if the feature is not active
        if (item.activeFeature && !checkActiveFeature(item.activeFeature)) {
            return false;
        }

        // Hide the menu item if its feature must be inactive, like the calendar without scheduling
        if (item.inActiveFeature && checkActiveFeature(item.inActiveFeature)) {
            return false;
        }

        return true;
    };

    // Returns the visible sub items of a main menu item
    const getVisibleSubItems = (menu: string) => {
        return (navigationConfig[menu]?.subMenu ?? []).filter(isSubItemVisible);
    };

    // Returns the single sub item a main menu item collapses into, when only one sub item is
    // left the main item takes its name and the sub navbar is not shown
    const getCollapsedSubItem = (menu: string) => {
        const visibleSubItems = getVisibleSubItems(menu);
        return navigationConfig[menu]?.collapseSingleSubItem && visibleSubItems.length === 1 ? visibleSubItems[0] : null;
    };

    // Renders the main menu items
    const renderMainMenu = () => {
        return Object.keys(navigationConfig).map(key => {
            const item = navigationConfig[key];

            // Hide this main menu item if its own feature is not active
            if (item.activeFeature && !checkActiveFeature(item.activeFeature)) {
                return null;
            }

            // Hide item when feature must be inactive
            if (item.inActiveFeature && checkActiveFeature(item.inActiveFeature)) {
                return null;
            }

            // Only render the main menu item if there is at least one visible sub item, if it has sub items
            const visibleSubItems = getVisibleSubItems(key);
            if (Array.isArray(item.subMenu) && visibleSubItems.length === 0) {
                return null;
            }

            // Find the role specific features in the submenus of this main menu item
            const roleSpecificSubFeatures = item.subMenu?.filter(subItem => subItem.activeFeature && roleFeatures.includes(subItem.activeFeature));

            // Check if at least one role specific feature is active
            const isAnyRoleSpecificFeatureActive = roleSpecificSubFeatures?.some(subItem => subItem.activeFeature && checkActiveFeature(subItem.activeFeature));

            // If no role specific features are active, do not show this main menu item
            if (roleSpecificSubFeatures && roleSpecificSubFeatures.length > 0 && !isAnyRoleSpecificFeatureActive) {
                return null;
            }

            // Determine the route of the main menu item, it's the first active sub menu item
            const firstVisibleSub = visibleSubItems.length > 0 ? visibleSubItems[0] : null;
            let route = firstVisibleSub ? firstVisibleSub.route : item.route;

            // A collapsed main menu item takes the name of its single visible sub item
            const collapsedSubItem = getCollapsedSubItem(key);
            const label = collapsedSubItem ? collapsedSubItem.name : item.name;

            // Check if the current menu item is active
            const isActive = currentLocation.pathname === item.route || (item.route !== '/' && currentLocation.pathname.startsWith(item.route)) || isSubrouteOfMenu(currentLocation.pathname, key);

            return (
                <Link key={key}
                    className={`navbar-item ${isActive ? 'active' : ''}`}  
                    to={route}>
                    {item.icon ? (
                        <FontAwesomeIcon icon={item.icon} />
                    ) : (
                        t(label)
                    )}
                </Link>
            );
        });
    };

    // Renders the sub menu items
    const renderSubMenu = (menu: string) => {

        // Get the routes of the sub menu that match the current path
        const matchingRoutes = (navigationConfig[menu]?.subMenu ?? [])
            .filter(item => currentLocation.pathname === item.route || currentLocation.pathname.startsWith(`${item.route}/`))
            .map(item => item.route);

        // Only the longest matching route is active, otherwise a sub route like
        // /contacts/work-locations would also activate its parent /contacts
        const activeRoute = matchingRoutes.sort((routeA, routeB) => routeB.length - routeA.length)[0];

        // Only render the visible sub items, so a hidden item never leaves a group gap
        const visibleItems = getVisibleSubItems(menu);

        return visibleItems.map((item, index) => {
            // Check if the current menu item is active
            const isActive = item.route === activeRoute || (currentLocation.pathname === '/' && item.route === '/home');

            // The first item of a new group gets a wider gap to the previous item
            const previousItem = index > 0 ? visibleItems[index - 1] : null;
            const startsGroup = Boolean(previousItem && item.group && previousItem.group !== item.group);

            return (
                <Link key={item.route}
                      className={`subnav-item ${isActive ? 'active' : ''} ${startsGroup ? 'group-start' : ''} ${item.alignRight ? 'align-right' : ''}`} 
                      to={item.route}>
                    {t(item.name)}
                </Link>
            );
        });
    }

    // Function that disables the visibility of the dropdown menu
    const toggleDropdown = () => {
        setDropdownVisible(!dropdownVisible);
    };

    // Only shows the navbar if the user is logged in
    return isLoggedIn && !hideNavBar ? (
        <div className='navbar-wrapper'>
            <TopBar />
            <nav className="navbar">
                <div className="navbar-container">
                    <div className="logo-container">
                        <Link to="/">
                            <img src={logo} alt="Logo" className='logo'/>
                        </Link>
                    </div>
                    {!trialWizard && (
                        // Hide nav bar buttons in the trial wizard
                        <>
                            <div className="main-menu-items">
                                {renderMainMenu()}
                            </div>
                            <div className='right-menu-items'>

                                {canSwitchEnvironment && (
                                    <div className='environment-switcher'>
                                        <Dropdown<Environment>
                                            options={environments}
                                            id='environment'
                                            name='environment'
                                            disabled_selected='Select environment'
                                            selectedOption={environments.find(environment => environment.environment_hash === environmentHash)}
                                            onChange={(selectedValue) => handleSwitchEnvironment(selectedValue)}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat='name'
                                            backendField='environment_hash'
                                            showSearch={true}
                                            allowNoneOption={false}
                                        />
                                    </div>
                                )}

                                <TimerButton />
                                <ThemeSwitcher />
                                <NotificationBell />

                                <div className='profile-container'
                                     ref={dropdownRef}
                                     onClick={toggleDropdown}>
                                    {profilePicture ? (
                                        <div style={{ backgroundImage: `url(${profilePicture})` }} 
                                             className='profile-image' />
                                    ) : userName && (
                                        <div style={{ paddingRight: '0.5rem' }}>
                                            <Avatar name={userName} size='2rem' />
                                        </div>
                                    )}
                                    <div className='navbar-item'>
                                        {userName}
                                    </div>
                                </div>
                                {dropdownVisible && (
                                    <div className={`dropdown-menu ${isTopNavbarEnabled ? 'topbar-enabled' : ''}`}>
                                        {allowedRights.includes('change_settings') && (
                                            <Link className='dropdown-item' 
                                                  to="/settings"
                                                  onClick={() => setDropdownVisible(false)}>
                                                    {t('nav.settings_label')}
                                            </Link>
                                        )}

                                        <Link className='dropdown-item' 
                                              to="/preferences"
                                              onClick={() => setDropdownVisible(false)}>
                                                {t('nav.personal_preferences_label')}
                                        </Link>

                                        <a className='dropdown-item'
                                           href="/kennisbank"
                                           target="_blank"
                                           rel="noopener noreferrer"
                                           onClick={() => setDropdownVisible(false)}>
                                                {t('nav.knowledge_base_link_label')}
                                        </a>

                                        <div className="dropdown-item"
                                             onClick={() => {
                                                setDropdownVisible(false);
                                                openKnowledgeBaseContactForm();
                                            }}>
                                            {t('nav.ask_question_label')}
                                        </div>

                                        <div className="dropdown-item" 
                                             onClick={() => {
                                                handleLogout({ logoutFromServer: true, showSessionAlert: false }); 
                                                setDropdownVisible(false);
                                            }}>
                                            {t('nav.log_out_label')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </nav>
            {selectedMenu && !getCollapsedSubItem(selectedMenu) && (
                <nav className="subnavbar">
                    <div className='container subnavbar-container'>
                        {renderSubMenu(selectedMenu)}
                    </div>
                </nav>
            )}
        </div>
    ) : null;
};

export default Navbar;